import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Check } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { money } from "../lib/ui";

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between gap-6 border-b border-line py-3">
    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink/50">{label}</span>
    <span className="text-right font-mono text-[13px] text-ink">{value}</span>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => fetchProduct(slug),
  });

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center bg-sand font-mono text-xs uppercase tracking-[0.3em] text-ink/40">Loading…</div>;
  }
  if (isError || !product) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-sand">
        <p className="font-display text-4xl text-ink">Not found.</p>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-rust">← Back home</Link>
      </div>
    );
  }

  const coa = product.coa;

  return (
    <div data-testid="product-detail" className="min-h-screen bg-sand pt-36">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <Link to="/#catalog" data-testid="back-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-ink">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} /> Back to Catalog
        </Link>

        <div className="mt-10 grid gap-12 pb-32 lg:grid-cols-2 lg:gap-20">
          {/* Left sticky image */}
          <div className="lg:sticky lg:top-36 lg:h-fit">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative border border-line corner-bracket"
            >
              <div className="absolute inset-0 spotlight z-[1]" />
              <img src={product.image} alt={product.name} className="relative aspect-square w-full object-cover" />
              <span className="absolute left-5 top-5 z-[3] font-mono text-sm tracking-widest text-ink/50">
                {String(product.rank).padStart(2, "0")} / 15
              </span>
            </motion.div>
          </div>

          {/* Right details */}
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">{product.category}</p>
            <h1 className="mt-4 font-display text-6xl leading-[0.85] tracking-tighter text-ink md:text-7xl">{product.name}</h1>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-ink/45">{product.abbreviation} · {product.size}</p>
            <p className="mt-8 max-w-lg font-body text-lg leading-relaxed text-ink/75">{product.description}</p>

            <div className="mt-8 flex flex-wrap gap-2">
              {product.research_areas.map((a) => (
                <span key={a} className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/60">
                  {a}
                </span>
              ))}
            </div>

            {/* Price + add to cart */}
            <div className="mt-10 flex flex-wrap items-center gap-6 border-y border-line py-6">
              <span className="font-display text-5xl text-ink">{money(product.price)}</span>
              <div className="flex items-center border border-ink">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-surface">
                  <Minus className="h-4 w-4" strokeWidth={2} />
                </button>
                <span className="w-10 text-center font-mono">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-surface">
                  <Plus className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              <button
                data-testid="detail-add-to-cart"
                onClick={() => addItem(product, qty)}
                className="group flex flex-1 items-center justify-center gap-2 border border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rust hover:border-rust"
              >
                Add to Cart — {money(product.price * qty)}
              </button>
            </div>

            {/* Specs */}
            <div className="mt-12">
              <h2 className="font-display text-3xl tracking-tight text-ink">Specifications</h2>
              <div className="mt-4">
                <SpecRow label="Sequence" value={<span className="break-all">{product.sequence}</span>} />
                <SpecRow label="Molecular Formula" value={product.molecular_formula} />
                <SpecRow label="Molecular Weight" value={product.molecular_weight} />
                <SpecRow label="CAS Number" value={product.cas_number} />
                <SpecRow label="Purity" value={product.purity} />
                <SpecRow label="Storage" value={<span className="max-w-xs">{product.storage}</span>} />
              </div>
            </div>

            {/* COA lab report */}
            <div data-testid="coa-report" className="mt-12 border-2 border-ink bg-surface p-6 md:p-8">
              <div className="flex items-center justify-between border-b-2 border-dashed border-ink/30 pb-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust">Certificate of Analysis</p>
                  <p className="mt-1 font-display text-2xl leading-none text-ink">Batch {coa.batch}</p>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-forest text-forest">
                  <Check className="h-6 w-6" strokeWidth={2} />
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                <SpecRow label="HPLC Purity" value={coa.purity_hplc} />
                <SpecRow label="Mass Spec" value={coa.mass_spec} />
                <SpecRow label="Endotoxin" value={coa.endotoxin} />
                <SpecRow label="Appearance" value={coa.appearance} />
                <SpecRow label="Test Date" value={coa.test_date} />
                <SpecRow label="Analyzing Lab" value={coa.lab} />
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.1em] text-ink/50">
                A physical copy of this Certificate of Analysis is included with your shipment.
              </p>
            </div>

            <div className="mt-8 border border-ruo/50 bg-ruo/5 p-5">
              <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.12em] text-ruo">
                For Research Use Only. Not for human or veterinary consumption. Not a drug or supplement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
